import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Heart, Bookmark, Building2, MessageSquare, Settings, LogOut, CreditCard } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/store/useAuth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const NAV = [
    { to: '/dashboard', end: true, label: 'Overview', icon: LayoutDashboard },
    { to: '/dashboard/saved-homes', label: 'Saved Homes', icon: Heart },
    { to: '/dashboard/saved-searches', label: 'Saved Searches', icon: Bookmark },
    ...(user?.kind === 'contractor' ? [{ to: '/dashboard/my-listings', label: 'My Listings', icon: Building2 }] : []),
    { to: '/dashboard/messages', label: 'Messages', icon: MessageSquare },
    { to: '/dashboard/billing', label: 'Billing', icon: CreditCard },
    { to: '/dashboard/profile', label: 'Profile', icon: Settings },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="lg:grid lg:grid-cols-[256px_1fr] lg:gap-8">
        <aside className="mb-6 lg:mb-0" data-testid="dashboard-sidebar">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:sticky lg:top-20">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <Avatar className="h-11 w-11">
                <AvatarImage src={user?.avatar} alt={user?.name} />
                <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">{user?.name}</p>
                <p className="truncate text-xs text-slate-500">{user?.email}</p>
              </div>
            </div>
            <nav className="mt-3 flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) => cn(
                    'flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50',
                  )}
                  data-testid={`dash-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <n.icon size={18} />{n.label}
                </NavLink>
              ))}
              <button
                onClick={async () => { await logout(); navigate('/'); }}
                className="flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
                data-testid="dash-logout"
              >
                <LogOut size={18} />Sign out
              </button>
            </nav>
          </div>
        </aside>
        <div className="min-w-0"><Outlet /></div>
      </div>
    </div>
  );
}
